// src/pages/CrearReporteComunidad.js
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaCamera, FaFileUpload, FaMicrophone, FaVideo } from 'react-icons/fa';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { registerMovement } from '../utils/registerMovement';
import { pickImage, pickVideo, pickAudioFile } from '../utils/mobileMedia';
import './PropuestasIncidencias.css';

const TIPOS_REPORTE = [
    {
        id: 'incidencia',
        titulo: 'Incidencia',
        descripcion: 'Reportar un problema',
        icono: '🚨',
    },
    {
        id: 'propuesta',
        titulo: 'Propuesta',
        descripcion: 'Sugerir una mejora',
        icono: '💡',
    },
];

const CATEGORIAS = [
    'Mantenimiento',
    'Seguridad',
    'Limpieza',
    'Servicios',
    'Áreas comunes',
    'Otro',
];

const CrearReporteComunidad = () => {
    const navigate = useNavigate();
    const { currentUser, userData } = useAuth();
    const { showNotification } = useNotification();
    const [tipo, setTipo] = useState('incidencia');
    const [categoria, setCategoria] = useState('');
    const [titulo, setTitulo] = useState('');
    const [ubicacion, setUbicacion] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [adjuntos, setAdjuntos] = useState({ foto: null, video: null, audio: null });
    const [enviando, setEnviando] = useState(false);

    const SIZE_LIMITS = {
        foto: 5 * 1024 * 1024, // 5 MB
        video: 30 * 1024 * 1024, // 30 MB
        audio: 10 * 1024 * 1024, // 10 MB
    };

    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
    const creadorId = currentUser?.uid || userData?.uid || null;
    const creadorNombre = userData?.nombre || currentUser?.displayName || userData?.nombreCompleto || userData?.nombreResidente || currentUser?.email || 'Residente';

    const esValido = useMemo(() => {
        return tipo && categoria && titulo.trim() && ubicacion.trim() && descripcion.trim();
    }, [tipo, categoria, titulo, ubicacion, descripcion]);

    const handleArchivoChange = (key, fileList) => {
        const file = fileList?.[0] ?? null;
        if (!file) {
            setAdjuntos((prev) => ({ ...prev, [key]: null }));
            return;
        }

        const max = SIZE_LIMITS[key] || SIZE_LIMITS.foto;
        if (file.size > max) {
            const toMb = (bytes) => Math.round((bytes / (1024 * 1024)) * 10) / 10;
            showNotification(`El ${key} excede el máximo permitido (${toMb(max)} MB).`, 'error');
            return;
        }

        setAdjuntos((prev) => ({ ...prev, [key]: file }));
    };

    const handlePickImage = async () => {
        try {
            const file = await pickImage();
            handleArchivoChange('foto', [file]);
            showNotification('Imagen seleccionada correctamente', 'success');
        } catch (error) {
            if (error.message !== 'Selección cancelada') {
                console.error('Error al seleccionar imagen:', error);
                showNotification('No se pudo seleccionar la imagen', 'error');
            }
        }
    };

    const handlePickVideo = async () => {
        try {
            const file = await pickVideo();
            handleArchivoChange('video', [file]);
            showNotification('Video seleccionado correctamente', 'success');
        } catch (error) {
            if (error.message !== 'Selección cancelada') {
                console.error('Error al seleccionar video:', error);
                showNotification('No se pudo seleccionar el video', 'error');
            }
        }
    };

    const handlePickAudio = async () => {
        try {
            const file = await pickAudioFile();
            handleArchivoChange('audio', [file]);
            showNotification('Audio seleccionado correctamente', 'success');
        } catch (error) {
            if (error.message !== 'Selección cancelada') {
                console.error('Error al seleccionar audio:', error);
                showNotification('No se pudo seleccionar el audio', 'error');
            }
        }
    };

    const handleEnviar = async (evento) => {
        evento.preventDefault();
        if (!esValido) {
            return;
        }
        if (!fraccionamientoId) {
            showNotification('No se pudo identificar el fraccionamiento del usuario.', 'error');
            return;
        }

        setEnviando(true);

        try {
            const ahora = Timestamp.now();
            const adjuntosPayload = {};

            const subirAdjunto = async (clave, archivo) => {
                if (!archivo) {
                    return null;
                }

                const carpeta = clave === 'foto' ? 'fotos' : clave === 'video' ? 'videos' : 'audios';
                const storagePath = `fraccionamientos/${fraccionamientoId}/propuestasIncidencias/${creadorId || 'anonimo'}/${carpeta}/${Date.now()}_${archivo.name}`;
                const archivoRef = ref(storage, storagePath);
                const snapshot = await uploadBytes(archivoRef, archivo);
                const url = await getDownloadURL(snapshot.ref);

                return {
                    url,
                    nombre: archivo.name,
                    peso: archivo.size,
                    contentType: archivo.type || null,
                    tipo: clave,
                    storagePath: snapshot.ref.fullPath,
                };
            };

            const [fotoSubida, videoSubido, audioSubido] = await Promise.all([
                subirAdjunto('foto', adjuntos.foto),
                subirAdjunto('video', adjuntos.video),
                subirAdjunto('audio', adjuntos.audio),
            ]);

            if (fotoSubida) {
                adjuntosPayload.fotos = [fotoSubida];
            }
            if (videoSubido) {
                adjuntosPayload.videos = [videoSubido];
            }
            if (audioSubido) {
                adjuntosPayload.audios = [audioSubido];
            }

            const reportesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'propuestasIncidencias');
            const docRef = await addDoc(reportesRef, {
                tipo,
                categoria,
                titulo: titulo.trim(),
                ubicacion: ubicacion.trim(),
                descripcion: descripcion.trim(),
                estado: 'pendiente',
                fechaCreacion: ahora,
                fechaActualizacion: ahora,
                fraccionamientoId,
                creadorId,
                creadoPorNombre: creadorNombre,
                creadoPorEmail: currentUser?.email || userData?.email || null,
                adjuntos: Object.keys(adjuntosPayload).length ? adjuntosPayload : null,
                tieneFoto: Boolean(fotoSubida),
                tieneVideo: Boolean(videoSubido),
                tieneAudio: Boolean(audioSubido),
            });

            showNotification('Reporte enviado con éxito.');
            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Nuevo ${tipo === 'propuesta' ? 'propuesta' : 'reporte'}: ${titulo.trim()}`,
                    descripcion: `${categoria} · ${ubicacion.trim()}`,
                    categoria: tipo === 'propuesta' ? 'Propuestas' : 'Incidencias',
                    tipoMovimiento: tipo === 'propuesta' ? 'nueva_propuesta' : 'nuevo_reporte',
                    origen: 'comunidad',
                    metadata: {
                        reporteId: docRef.id,
                        tipoReporte: tipo,
                        categoriaReporte: categoria,
                        ubicacionReporte: ubicacion.trim(),
                        tieneAdjuntos: Boolean(fotoSubida || videoSubido || audioSubido),
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('CrearReporteComunidad: error al registrar movimiento', logError);
            }
            navigate('/comunidad/propuestas');
        } catch (error) {
            console.error('Error al registrar el reporte:', error);
            showNotification('No se pudo registrar el reporte. Inténtalo nuevamente.', 'error');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <div className="crear-reporte-page">
            <form className="crear-reporte-card" onSubmit={handleEnviar}>
                <div className="crear-reporte-hero">
                    <div className="hero-text">
                        <h1>Nuevo Reporte</h1>
                        <p>Completa la información del reporte</p>
                    </div>
                </div>

                <section className="reporte-section">
                    <h2 className="section-title">Tipo de reporte *</h2>
                    <div className="tipo-grid">
                        {TIPOS_REPORTE.map((opcion) => (
                            <button
                                key={opcion.id}
                                type="button"
                                className={`tipo-card ${tipo === opcion.id ? 'active' : ''}`}
                                onClick={() => setTipo(opcion.id)}
                            >
                                <div className="tipo-icon" aria-hidden="true">{opcion.icono}</div>
                                <h3>{opcion.titulo}</h3>
                                <p>{opcion.descripcion}</p>
                            </button>
                        ))}
                    </div>
                </section>

                <section className="reporte-section">
                    <label className="control-label" htmlFor="categoria">Categoría *</label>
                    <div className="select-wrapper">
                        <select
                            id="categoria"
                            value={categoria}
                            onChange={(e) => setCategoria(e.target.value)}
                        >
                            <option value="">Selecciona una categoría</option>
                            {CATEGORIAS.map((cat) => (
                                <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                            ))}
                        </select>
                    </div>
                    <span className="control-helper">Elige la categoría que mejor describe la incidencia o propuesta.</span>
                </section>

                <section className="reporte-section grid-2">
                    <div>
                        <label className="control-label" htmlFor="titulo">Título *</label>
                        <input
                            id="titulo"
                            type="text"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            maxLength={120}
                            placeholder="Ej: Fuga de agua en área común"
                            autoCorrect="on"
                            spellCheck="true"
                            autoCapitalize="sentences"
                        />
                        <span className="control-helper">Usa un título corto y claro (máx. 120 caracteres).</span>
                    </div>
                    <div>
                        <label className="control-label" htmlFor="ubicacion">Ubicación *</label>
                        <input
                            id="ubicacion"
                            type="text"
                            value={ubicacion}
                            onChange={(e) => setUbicacion(e.target.value)}
                            maxLength={140}
                            placeholder="Ej: Entrada principal, Calle Las Flores #12"
                            autoCorrect="on"
                            spellCheck="true"
                            autoCapitalize="sentences"
                        />
                        <span className="control-helper">Indica el punto exacto o referencia para localizar el problema.</span>
                    </div>
                </section>

                <section className="reporte-section">
                    <label className="control-label" htmlFor="descripcion">Descripción detallada *</label>
                    <textarea
                        id="descripcion"
                        rows={5}
                        maxLength={800}
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        placeholder="Describe con detalle la situación..."
                        autoCorrect="on"
                        spellCheck="true"
                        autoCapitalize="sentences"
                    ></textarea>
                    <span className="control-helper">Incluye pasos para reproducirlo, horarios y personas involucradas si aplica (máx. 800 caracteres).</span>
                </section>

                <section className="reporte-section">
                    <div className="section-heading">
                        <h2 className="section-title">Evidencias (Opcional)</h2>
                        <span className="section-hint">Adjunta foto (máx. 5 MB), video (máx. 30 MB) o audio (máx. 10 MB)</span>
                    </div>
                    <div className="evidencia-grid">
                        <div className="evidencia-card">
                            <button
                                type="button"
                                onClick={handlePickImage}
                                className="evidencia-btn"
                            >
                                <FaCamera />
                                <span>Foto</span>
                            </button>
                            <small className="evidencia-hint">PNG, JPG · máx. 5 MB</small>
                            {adjuntos.foto && <small className="evidencia-file-name">{adjuntos.foto.name}</small>}
                        </div>

                        <div className="evidencia-card">
                            <button
                                type="button"
                                onClick={handlePickVideo}
                                className="evidencia-btn"
                            >
                                <FaVideo />
                                <span>Video</span>
                            </button>
                            <small className="evidencia-hint">MP4/MOV · máx. 30 MB</small>
                            {adjuntos.video && <small className="evidencia-file-name">{adjuntos.video.name}</small>}
                        </div>

                        <div className="evidencia-card">
                            <button
                                type="button"
                                onClick={handlePickAudio}
                                className="evidencia-btn"
                            >
                                <FaMicrophone />
                                <span>Audio</span>
                            </button>
                            <small className="evidencia-hint">MP3/M4A · máx. 10 MB</small>
                            {adjuntos.audio && <small className="evidencia-file-name">{adjuntos.audio.name}</small>}
                        </div>
                    </div>
                </section>

                <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={() => navigate('/comunidad/propuestas')}>
                        Cancelar
                    </button>
                    <button type="submit" className="btn-primary btn-with-icon" disabled={!esValido || enviando}>
                        <FaFileUpload />
                        {enviando ? 'Enviando...' : 'Enviar reporte'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CrearReporteComunidad;
